import * as React from "react";
import {
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import Button from "@mui/material/Button";
import {toast} from "react-toastify";
import LoadingDialog from "../../components/loadingDialog";
import {emptyCap, fetchGroupCapCampaigns, upsertGroupCap} from "./groupCapsService";
import moment from "moment";

const DAYS = [
    {label: "Monday", key: "monday", col: 1},
    {label: "Tuesday", key: "tuesday", col: 2},
    {label: "Wednesday", key: "wednesday", col: 3},
    {label: "Thursday", key: "thursday", col: 4},
    {label: "Friday", key: "friday", col: 5},
    {label: "Saturday", key: "saturday", col: 6},
    {label: "Sunday", key: "sunday", col: 7},
] as const;

const numFieldSx = {
    width: 100,
    "& .MuiInputBase-root": {height: 30, fontSize: 13},
    "& .MuiInputBase-input": {py: 0.5, px: 1},
};

function getDayLabel(column: number) {
    const today = moment();
    let dow = parseInt(today.format("e"), 10);
    if (dow === 0) {
        dow = 7;
    }
    let diff = dow - column;
    if (column < dow) {
        diff = diff - 7;
    }
    const date = today.clone().add(-diff, "day").format("MM/DD");
    const highlight = diff === 0 || diff === 7;
    return {date, highlight};
}

function ensureCaps(group: any) {
    const g = {...group};
    const keys = [
        "hourlyCap", "dailyCap", "monthlyCap",
        "mondayCap", "tuesdayCap", "wednesdayCap", "thursdayCap", "fridayCap", "saturdayCap", "sundayCap",
        "mondayHourlyCap", "tuesdayHourlyCap", "wednesdayHourlyCap", "thursdayHourlyCap", "fridayHourlyCap", "saturdayHourlyCap", "sundayHourlyCap",
        "mondayPacingCap", "tuesdayPacingCap", "wednesdayPacingCap", "thursdayPacingCap", "fridayPacingCap", "saturdayPacingCap", "sundayPacingCap",
    ];
    keys.forEach((k) => {
        g[k] = g[k] ? {...g[k]} : emptyCap();
    });
    g.name = g.name || "";
    g.notes = g.notes || "";
    g.emails = g.emails || "";
    g.pacingProvider = g.pacingProvider || "";
    g.partnerId = g.partnerId != null ? g.partnerId : 0;
    return g;
}

function CapCheck({cap, onChange}: {cap: any; onChange: (cap: any) => void}) {
    return (
        <Checkbox
            size="small"
            checked={!!cap?.isOn}
            onChange={(e) => onChange({...cap, isOn: e.target.checked})}
            sx={{p: 0.25}}
        />
    );
}

function CapValue({cap, onChange}: {cap: any; onChange: (cap: any) => void}) {
    return (
        <TextField
            type="number"
            size="small"
            value={cap?.value ?? ""}
            onChange={(e) => onChange({
                ...cap,
                value: e.target.value === "" ? null : Number(e.target.value),
            })}
            sx={numFieldSx}
        />
    );
}

const td: React.CSSProperties = {verticalAlign: "middle", padding: "4px 6px", whiteSpace: "nowrap"};

export default function GroupCapUpsert(params: {group: any; setGroup: any; reload: () => void}) {
    const create = !params.group?.id;
    const [loading, setLoading] = React.useState(true);
    const [group, setGroup] = React.useState<any>(ensureCaps(params.group || {}));
    const [campaignsText, setCampaignsText] = React.useState("");

    React.useEffect(() => {
        const load = async () => {
            setGroup(ensureCaps(params.group || {}));
            if (!params.group?.id) {
                setCampaignsText("");
                setLoading(false);
                return;
            }
            setLoading(true);
            const batches = await fetchGroupCapCampaigns([params.group.id]);
            const campaigns = batches[0]?.campaigns || [];
            setCampaignsText(campaigns.map((c: any) => c.hpCampaignId).join("\n"));
            setLoading(false);
        };
        load().then(() => {});
    }, [params.group]);

    const handleClose = (_?: any, reason?: any) => {
        if (reason && reason === "backdropClick") {
            return;
        }
        params.setGroup(null);
    };

    const setCap = (field: string, cap: any) => {
        setGroup((g: any) => ({...g, [field]: cap}));
    };

    const handleSave = async () => {
        if (!(group.name || "").trim()) {
            toast.error("Please provide a name");
            return;
        }
        if (!(group.notes || "").trim()) {
            toast.error("Please provide notes");
            return;
        }
        const hpCampaignIds = campaignsText
            .trim()
            .split(/\r?\n/)
            .map((x) => x.trim())
            .filter(Boolean)
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n) && n > 0);

        if (!hpCampaignIds.length) {
            toast.error("Please provide at least 1 campaign id");
            return;
        }

        const input: any = {
            name: group.name.trim(),
            notes: group.notes || "",
            emails: group.emails || "",
            pacingProvider: group.pacingProvider || "",
            hpCampaignIds,
            hourlyCap: group.hourlyCap,
            dailyCap: group.dailyCap,
            monthlyCap: group.monthlyCap,
            mondayCap: group.mondayCap,
            tuesdayCap: group.tuesdayCap,
            wednesdayCap: group.wednesdayCap,
            thursdayCap: group.thursdayCap,
            fridayCap: group.fridayCap,
            saturdayCap: group.saturdayCap,
            sundayCap: group.sundayCap,
            mondayHourlyCap: group.mondayHourlyCap,
            tuesdayHourlyCap: group.tuesdayHourlyCap,
            wednesdayHourlyCap: group.wednesdayHourlyCap,
            thursdayHourlyCap: group.thursdayHourlyCap,
            fridayHourlyCap: group.fridayHourlyCap,
            saturdayHourlyCap: group.saturdayHourlyCap,
            sundayHourlyCap: group.sundayHourlyCap,
            mondayPacingCap: group.mondayPacingCap,
            tuesdayPacingCap: group.tuesdayPacingCap,
            wednesdayPacingCap: group.wednesdayPacingCap,
            thursdayPacingCap: group.thursdayPacingCap,
            fridayPacingCap: group.fridayPacingCap,
            saturdayPacingCap: group.saturdayPacingCap,
            sundayPacingCap: group.sundayPacingCap,
        };
        if (!create) {
            input.id = group.id;
            input.partnerId = Number(group.partnerId || 0);
        }

        setLoading(true);
        const id = await upsertGroupCap(input);
        setLoading(false);
        if (id) {
            toast.success(create ? `${group.name} created successfully.` : `${group.name} updated successfully.`);
            handleClose();
            params.reload();
        }
    };

    if (!params.group) {
        return <></>;
    }

    return (
        <Dialog
            open={!!params.group}
            onClose={handleClose}
            maxWidth={false}
            PaperProps={{sx: {width: "fit-content", maxWidth: "95vw"}}}
        >
            <DialogTitle>{create ? "Create Group" : `Update Group ${group.name}`}</DialogTitle>
            <DialogContent>
                {loading ? <LoadingDialog/> : (
                    <>
                        <Box sx={{mb: 1}}>
                            Name:{" "}
                            <TextField
                                size="small"
                                required
                                fullWidth
                                value={group.name}
                                onChange={(e) => setGroup({...group, name: e.target.value})}
                                sx={{"& .MuiInputBase-root": {height: 32, fontSize: 13}}}
                            />
                        </Box>
                        <Box sx={{mb: 1.5}}>
                            Notes:{" "}
                            <TextField
                                size="small"
                                required
                                fullWidth
                                value={group.notes}
                                onChange={(e) => setGroup({...group, notes: e.target.value})}
                                sx={{"& .MuiInputBase-root": {height: 32, fontSize: 13}}}
                            />
                        </Box>

                        <Box component="table" sx={{
                            width: "auto",
                            borderCollapse: "collapse",
                            mb: 1.5,
                            fontSize: 13,
                            "& td": td,
                        }}>
                            <tbody>
                            <tr>
                                <td>Hourly</td>
                                <td><CapCheck cap={group.hourlyCap} onChange={(c) => setCap("hourlyCap", c)}/></td>
                                <td><CapValue cap={group.hourlyCap} onChange={(c) => setCap("hourlyCap", c)}/></td>
                                <td/><td/><td/>
                                {!create && (
                                    <>
                                        <td>Pause with ID</td>
                                        <td/>
                                        <td>
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={group.partnerId ?? 0}
                                                onChange={(e) => setGroup({...group, partnerId: Number(e.target.value || 0)})}
                                                sx={numFieldSx}
                                            />
                                        </td>
                                    </>
                                )}
                            </tr>
                            <tr>
                                <td>Daily</td>
                                <td><CapCheck cap={group.dailyCap} onChange={(c) => setCap("dailyCap", c)}/></td>
                                <td><CapValue cap={group.dailyCap} onChange={(c) => setCap("dailyCap", c)}/></td>
                            </tr>
                            <tr>
                                <td>Monthly</td>
                                <td><CapCheck cap={group.monthlyCap} onChange={(c) => setCap("monthlyCap", c)}/></td>
                                <td><CapValue cap={group.monthlyCap} onChange={(c) => setCap("monthlyCap", c)}/></td>
                                <td/><td/><td/>
                                <td>MTD Pace</td>
                                <td/>
                                <td>
                                    <Select
                                        size="small"
                                        displayEmpty
                                        value={group.pacingProvider || ""}
                                        onChange={(e) => setGroup({...group, pacingProvider: e.target.value})}
                                        sx={{minWidth: 100, height: 30, fontSize: 13}}
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        <MenuItem value="AIU">AIU</MenuItem>
                                        <MenuItem value="CTU">CTU</MenuItem>
                                    </Select>
                                </td>
                            </tr>
                            {DAYS.map((day) => {
                                const {date, highlight} = getDayLabel(day.col);
                                const capKey = `${day.key}Cap`;
                                const hourlyKey = `${day.key}HourlyCap`;
                                const pacingKey = `${day.key}PacingCap`;
                                return (
                                    <tr key={day.key}>
                                        <td>
                                            {day.label}{" "}
                                            <b style={highlight ? {background: "yellow"} : undefined}>{date}</b>
                                        </td>
                                        <td><CapCheck cap={group[capKey]} onChange={(c) => setCap(capKey, c)}/></td>
                                        <td><CapValue cap={group[capKey]} onChange={(c) => setCap(capKey, c)}/></td>
                                        <td>Hourly</td>
                                        <td><CapCheck cap={group[hourlyKey]} onChange={(c) => setCap(hourlyKey, c)}/></td>
                                        <td><CapValue cap={group[hourlyKey]} onChange={(c) => setCap(hourlyKey, c)}/></td>
                                        <td>MTD Pace</td>
                                        <td><CapCheck cap={group[pacingKey]} onChange={(c) => setCap(pacingKey, c)}/></td>
                                        <td>
                                            <Box sx={{display: "inline-flex", alignItems: "center", gap: 0.5}}>
                                                <CapValue cap={group[pacingKey]} onChange={(c) => setCap(pacingKey, c)}/>
                                                <span>%</span>
                                            </Box>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </Box>

                        <Box sx={{mb: 1}}>
                            Campaign Ids:
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                size="small"
                                value={campaignsText}
                                onChange={(e) => setCampaignsText(e.target.value)}
                                sx={{
                                    "& .MuiInputBase-root": {alignItems: "flex-start"},
                                    "& .MuiInputBase-input": {fontSize: 13, overflow: "auto !important"},
                                }}
                            />
                        </Box>
                        <Box>
                            Email Alerts:
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                                value={group.emails}
                                onChange={(e) => setGroup({...group, emails: e.target.value})}
                                sx={{
                                    "& .MuiInputBase-root": {alignItems: "flex-start"},
                                    "& .MuiInputBase-input": {fontSize: 13, overflow: "auto !important"},
                                }}
                            />
                        </Box>
                    </>
                )}
            </DialogContent>
            {!loading && (
                <DialogActions>
                    <Button sx={{color: "red"}} onClick={handleClose}>Close</Button>
                    <Button sx={{color: "green"}} onClick={handleSave}>{create ? "Create" : "Update"}</Button>
                </DialogActions>
            )}
        </Dialog>
    );
}
